import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateRegistry } from '../../../../services/translate/translate.registry';
import {
  ListingDataSource,
  ListingPageModel
} from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingTableComponent } from '../../../../shared/components/listing-table/listing-table.component';
import { TableData } from '../../../../shared/types/api-response.types';
import { OrderDirection } from '../../devices/components/devices.models';
import { FieldOperationsService } from './field-operations.service';
import { type FieldOperationsList, type FieldOperationsListItem } from './field-operations.types';
import {
  FieldOperationFilterName,
  FIELD_OPERATIONS_SEARCH_TYPES,
  FieldOperationSortFieldName,
  fieldOperationsTableSortFields,
  TABLE_COLUMN
} from './field-operations-listing-constants';
import { FieldOperationsSidePanelComponent } from './side-panel/field-operations-side-panel.component';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { FieldOperationsListingFiltersSourceService } from './field-operations-listing-filters-source.service';
import { ExportRow } from '@portal/shared/types/export.types';
import { TimezoneService } from 'src/app/services/utility/timezone.service';

@Injectable()
export class FieldOperationsServiceServiceListingModelService
  extends ListingPageModelAbstract
  implements ListingPageModel
{
  private readonly fieldOperationsServiceService = inject(FieldOperationsService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  private readonly timezoneService = inject(TimezoneService);

  readonly pageTitle = TranslateRegistry.get.INSTALLATION_HISTORY.TITLE.FIELD_OPERATIONS;
  readonly searchTypeList = computed(() => FIELD_OPERATIONS_SEARCH_TYPES);
  readonly selectedSearchType = computed(() => FIELD_OPERATIONS_SEARCH_TYPES[0]);
  readonly searchTextMaxLength = 64;

  readonly tableColumns = signal(TABLE_COLUMN);
  readonly dataSource: ListingDataSource<FieldOperationsList> = {
    getAll: tableState => {
      return this.fieldOperationsServiceService.readList({
        ...tableState,
        filters: (tableState.filters ?? []).map(f => ({
          ...f,
          filteredValues:
            f.filterType === FieldOperationFilterName.InstallationDateTimeUtc && f.filteredValues.length === 2
              ? this.timezoneService.getShiftedDatepickerRangeStringified({
                  start: new Date(f.filteredValues[0]),
                  end: new Date(f.filteredValues[1])
                })
              : f.filteredValues
        }))
      });
    },
    total: 0
  };
  readonly tableCmp = ListingTableComponent;
  readonly sorting = computed(() => ({
    columnName: FieldOperationSortFieldName.InstallationDateTimeUtc,
    orderDirection: OrderDirection.DESC
  }));
  readonly sortField = fieldOperationsTableSortFields;
  readonly selectionFieldName = 'deviceUID';
  readonly sidePanelTitle = TranslateRegistry.get.INSTALLATIONS_SIDE_PANEL.TITLE.PROGRAMMING_DETAILS;
  readonly sidePanelBodyCmp = FieldOperationsSidePanelComponent;

  readonly exportConfig = {
    fileName: 'Export-FieldOperations',
    mapData: (data: TableData<FieldOperationsListItem>): ExportRow[] => {
      return data.data.flatMap(fieldOperation => {
        if (fieldOperation.childItems.length === 0) {
          return [
            {
              installationDateTime: { title: 'OperationDateTime', value: fieldOperation.installationDateTime },
              mtuDeviceLabel: { title: 'CommDeviceID', value: fieldOperation.mtuDeviceLabel },
              installationReason: { title: 'Reason', value: fieldOperation.installationReason },
              meterSerialNumber: { title: 'MeterSerialNumber', value: '' },
              meterModel: { title: 'MeterModel', value: '' },
              port: { title: 'Port', value: '' },
              serviceLocationIds: { title: 'ServiceLocationNumber', value: '' },
              wakeupDateTime: { title: 'WakeUpReceived', value: '' },
              initialReadDateTime: { title: 'ReadingReceived', value: '' },
              installedBy: { title: 'InstalledBy', value: fieldOperation.installedBy }
            }
          ];
        }

        return fieldOperation.childItems.map(childItem => ({
          installationDateTime: { title: 'OperationDateTime', value: fieldOperation.installationDateTime },
          mtuDeviceLabel: { title: 'CommDeviceID', value: fieldOperation.mtuDeviceLabel },
          installationReason: { title: 'Reason', value: fieldOperation.installationReason },
          meterSerialNumber: { title: 'MeterSerialNumber', value: childItem.serialNumber },
          meterModel: { title: 'MeterModel', value: childItem.model },
          port: { title: 'Port', value: childItem.port },
          serviceLocationIds: { title: 'ServiceLocationNumber', value: childItem.serviceLocationIds },
          wakeupDateTime: {
            title: 'WakeUpReceived',
            value: childItem.wakeupDateTimeUtc
              ? this.timezoneService.formatLong(childItem.wakeupDateTimeUtc)
              : childItem.wakeupDateTimeUtc
          },
          initialReadDateTime: {
            title: 'ReadingReceived',
            value: childItem.initialReadDateTimeUtc
              ? this.timezoneService.formatLong(childItem.initialReadDateTimeUtc)
              : childItem.initialReadDateTimeUtc
          },
          installedBy: { title: 'InstalledBy', value: fieldOperation.installedBy }
        }));
      });
    }
  };

  readonly pageId = ListingPageId.FieldOperationsMetering;
  readonly filterSource = inject(FieldOperationsListingFiltersSourceService);

  constructor() {
    super();
    const listingPageModelRegistry = this.listingPageModelRegistry;

    listingPageModelRegistry.add(this.pageId, this);
  }
}
